import type { ReactNode } from 'react';
import { Button, Card, Text, Tooltip } from '@shopify/polaris';
import { ExternalSmallIcon, RefreshIcon } from '@shopify/polaris-icons';
import ProductThumbnail from '../../app/components/ProductThumbnail';
import type { ProductDetails } from '../../app/models/Product';
import './ProductSummaryCard.css';

type Props = {
    product: Pick<ProductDetails, 'title' | 'featuredImage' | 'sku' | 'vendor' | 'totalFitmentCount' | 'isAtdProduct'> | null;
    isUniversal: boolean;
    adminUrl: string;
    children?: ReactNode;
};

export default function ProductSummaryCard({ product, isUniversal, adminUrl, children }: Props) {
    return (
        <Card>
            <section className="pf-product-summary" aria-label="Product summary">
                <div className="pf-product-summary__row">
                    <div className="pf-product-summary__image">
                        <ProductThumbnail source={product?.featuredImage} alt={product?.title ?? 'Product'} size="medium" />
                    </div>
                    <dl className="pf-product-summary__field pf-product-summary__sku">
                        <dt><Text as="span" variant="bodySm" tone="subdued">SKU</Text></dt>
                        <dd><Text as="span" variant="bodyMd" fontWeight="medium">{product?.sku || '—'}</Text></dd>
                    </dl>
                    <dl className="pf-product-summary__field pf-product-summary__vendor">
                        <dt><Text as="span" variant="bodySm" tone="subdued">Vendor</Text></dt>
                        <dd>
                            <Text as="span" variant="bodyMd" fontWeight="medium">{product?.vendor || '—'}</Text>
                            {product?.isAtdProduct && (
                                <div className="pf-product-summary__source">
                                    <Tooltip content="ATD source product synced with Shopify">
                                        <span className="pf-product-summary__sync">
                                            <RefreshIcon width={16} height={16} aria-hidden="true" />
                                            <Text as="span" variant="bodySm">ATD synced</Text>
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                        </dd>
                    </dl>
                    <dl className="pf-product-summary__field pf-product-summary__fitments">
                        <dt><Text as="span" variant="bodySm" tone="subdued">Fitments</Text></dt>
                        <dd>
                            {product && isUniversal
                                ? <Text as="span" variant="bodyMd" fontWeight="medium">Universal product</Text>
                                : <span className="pf-product-summary__count"><Text as="span" variant="headingLg">{product ? product.totalFitmentCount.toLocaleString() : '…'}</Text></span>}
                        </dd>
                    </dl>
                    <div className="pf-product-summary__action">
                        {adminUrl && (
                            <Button url={adminUrl} target="_blank" variant="secondary" icon={ExternalSmallIcon} fullWidth>
                                Shopify admin
                            </Button>
                        )}
                    </div>
                </div>
                {children && <div className="pf-product-summary__scope"><div>{children}</div></div>}
            </section>
        </Card>
    );
}
